import dayjs from 'dayjs';
import { isNil } from 'ramda';
import { FULL_TIME_SPAN_REGEX } from 'src/constants/regExps';
export var RoundTo;
(function (RoundTo) {
    RoundTo["Hours"] = "hours";
    RoundTo["Minutes"] = "minutes";
})(RoundTo || (RoundTo = {}));
export const timeSpanToHoursDecimal = (timeSpan, roundTo = 2) => {
    if (typeof timeSpan === 'string') {
        const isNegative = timeSpan.startsWith('-');
        const withoutMinuses = timeSpan.replaceAll('-', '');
        const formattedTimeSpan = isNegative ? `-${withoutMinuses}` : withoutMinuses;
        const asHours = dayjs.duration(formattedTimeSpan).asHours();
        return !isNil(roundTo) ? Math.round(asHours * 10 ** roundTo) / 10 ** roundTo : asHours;
    }
    return timeSpan;
};
export const getRoundedTimeSpan = (timeSpan, roundTo) => {
    if (roundTo === RoundTo.Hours) {
        return String(timeSpanToHoursDecimal(timeSpan, 0));
    }
    else if (roundTo === RoundTo.Minutes) {
        const isNegative = timeSpan.startsWith('-');
        timeSpan = isNegative ? timeSpan.replaceAll('-', '') : timeSpan;
        const [hh, mm, ss] = timeSpan.split(':');
        const seconds = +mm * 60 + +ss;
        const roundedMinutes = Math.round(seconds / 60);
        const hours = +hh + Math.floor(roundedMinutes / 60);
        const minutes = roundedMinutes % 60;
        const hhRounded = hours < 10 ? `0${hours}` : hours;
        const mmRounded = minutes < 10 ? `0${minutes}` : minutes;
        return `${isNegative ? '-' : ''}${hhRounded}:${mmRounded}`;
    }
    return timeSpan;
};
export const decimalToTimeSpan = (decimal, roundTo) => {
    const isNegative = decimal < 0;
    const absDecimal = Math.abs(decimal);
    const hours = Math.floor(absDecimal);
    const minutes = Math.floor((absDecimal - hours) * 60);
    const seconds = Math.floor(((absDecimal - hours) * 60 - minutes) * 60);
    const hh = hours < 10 ? `0${hours}` : hours;
    const mm = minutes < 10 ? `0${minutes}` : minutes;
    const ss = seconds < 10 ? `0${seconds}` : seconds;
    const timeSpan = `${hh}:${mm}:${ss}`;
    return `${isNegative ? '-' : ''}${roundTo ? getRoundedTimeSpan(timeSpan, roundTo) : timeSpan}`;
};
export const decimalToTimeSpanWithDays = (decimal) => {
    const isNegative = decimal < 0;
    const absDecimal = Math.abs(decimal);
    const days = Math.floor(absDecimal / 24);
    const hours = Math.floor(absDecimal - days * 24);
    const minutes = Math.floor(((absDecimal - days * 24) - hours) * 60);
    const seconds = Math.floor((((absDecimal - days * 24) - hours) * 60 - minutes) * 60);
    const dd = days < 10 ? `0${days}` : days;
    const hh = hours < 10 ? `0${hours}` : hours;
    const mm = minutes < 10 ? `0${minutes}` : minutes;
    const ss = seconds < 10 ? `0${seconds}` : seconds;
    const daysValue = days > 0 ? `${dd}.` : '';
    const timeSpan = `${daysValue}${hh}:${mm}:${ss}`;
    return `${isNegative ? '-' : ''}${timeSpan}`;
};
export const timeSpanWithDays = (timeSpan) => {
    const [hh, mm, ss = '00'] = timeSpan.split(':');
    if (hh.includes('.'))
        return timeSpan;
    const isNegative = timeSpan.startsWith('-');
    const hours = +hh;
    const minutes = +mm;
    const seconds = +ss;
    const days = Math.floor(hours / 24);
    const hoursInDays = hours - days * 24;
    const hhDays = hoursInDays < 10 ? `0${hoursInDays}` : hoursInDays;
    const mmDays = minutes < 10 ? `0${minutes}` : minutes;
    const ssDays = seconds < 10 ? `0${seconds}` : seconds;
    const daysValue = days > 0 ? `${days}.` : '';
    return `${isNegative ? '-' : ''}${daysValue}${hhDays}:${mmDays}:${ssDays}`;
};
// 1.10:00:00 -> 34:00:00
export const timeSpanWithDaysToUsual = (timeSpan) => {
    const [ddhh, mm, ss = '00'] = timeSpan.split(':');
    const isNegative = timeSpan.startsWith('-');
    const [dd, hh] = ddhh.includes('.') ? ddhh.split('.') : ['0', ddhh];
    const days = Math.abs(+dd);
    const hours = Math.abs(+hh);
    const hoursTotal = days * 24 + hours;
    const hhTotal = hoursTotal < 10 ? `0${hoursTotal}` : hoursTotal;
    const hhFinal = isNegative ? `-${hhTotal}` : hhTotal;
    return `${hhFinal}:${mm}:${ss}`;
};
export const splitTimeSpan = (timeSpan) => {
    const match = FULL_TIME_SPAN_REGEX.exec(timeSpan.trim());
    if (!match)
        return null;
    const [fullMatch, daysStr, hoursStr, minutesStr, secondsStr] = match;
    const isNegative = fullMatch.startsWith('-');
    const days = daysStr ? parseInt(daysStr, 10) : 0;
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);
    const seconds = secondsStr ? parseInt(secondsStr, 10) : 0;
    const multiplier = isNegative ? -1 : 1;
    return {
        days: days * multiplier,
        hours: hours * multiplier,
        minutes: minutes * multiplier,
        seconds: seconds * multiplier
    };
};
export const durationBetweenDates = (start, end) => {
    const startDate = dayjs(start);
    const endDate = dayjs(end);
    const duration = endDate.diff(startDate);
    const durationObj = dayjs.duration(duration);
    const hours = Math.floor(durationObj.asHours());
    const minutes = durationObj.minutes();
    const seconds = durationObj.seconds();
    const hh = hours < 10 ? `0${hours}` : hours;
    const mm = minutes < 10 ? `0${minutes}` : minutes;
    const ss = seconds < 10 ? `0${seconds}` : seconds;
    return `${hh}:${mm}:${ss}`;
};
export const timeSpanToDayjs = (v) => {
    if (!v)
        return null;
    return dayjs(v, v.split(':').length === 2 ? 'HH:mm' : 'HH:mm:ss');
};
